import { API_BASE_URL, AUTH_BASE_URL } from '../config/serverApiConfig';
import errorHandler from '../request/errorHandler';
import successHandler from '../request/successHandler';
import { api } from './api';
import axios from 'axios';
import { grantExceedsFrom403 } from './capability.service';
import { managedByFrom409 } from './delegation.service';

// `addUser` used to POST an always-empty FormData to `user/create` (404, no successor at
// that path). It had no call sites; `createUser` below is the live one, and it already
// posts to `AUTH_BASE_URL + users/create`. I.e. `POST /r/v2/auth/users/create`, which is
// served.

export interface RoleName {
  name: string;
  description?: string;
  /**
   * Present on every one of the calling workspace's custom roles: a workspace may define
   * several, so expect N entries carrying this, not at most one. It is the only safe way
   * to identify a custom role: the name is unpredictable, and the same name in another
   * workspace is a different role. Do not cache this list across a workspace switch.
   */
  custom_role_id?: number;
  /**
   * Whether the signed-in caller may grant this role, answered by the server from the same
   * `may_grant` its write path enforces, so a picker filtered on this cannot offer a role
   * the invitation or role-change endpoint will refuse.
   *
   * The catalogue is a *label vocabulary* before it is a picker: it still names SUPERADMIN
   * to a Superadmin so their own membership renders, while nobody (themselves included)
   * may grant it. Filter pickers on this; never filter the list used to display somebody's
   * current role, or the one account that matters most shows a blank.
   *
   * Optional because a backend predating the field omits it. Treat only an explicit
   * `false` as "cannot grant": filtering on a missing field would empty the dropdown.
   */
  assignable?: boolean;
}

export const getRoleNames = async (workspaceId?: number): Promise<RoleName[]> => {
  try {
    const response = await api().get(
      `${API_BASE_URL}r/v2/auth/roles/name`,
      workspaceId != null ? { 
        headers: { 'X-Workspace-Id': workspaceId } 
      } : undefined
    );
    return response.data?.roles ?? [];
  } catch (error) {
    errorHandler(error);
    return [];
  }
};

const getWorkspaceId = (): string | null => {
  const auth = sessionStorage.getItem('auth');
  return auth ? JSON.parse(auth).current?.default_workspace?.id ?? null : null;
};

export interface RRealmUser {
  id: string;
  username?: string;
  email: string;
  firstName?: string;
  lastName?: string;
  enabled?: boolean;
  emailVerified?: boolean;
  createdTimestamp?: number;
  role?: string;
  /**
   * Whether an authenticator app is enrolled, read live from Keycloak. It is the only
   * per-account second-factor state any administrative route reports: the row on
   * `GET /workspace/users` carries `twoFactorEnabled`, but that reads a mirror table which
   * lags: an account with an authenticator enrolled still reads `false` there, and the
   * email factor is not reported to administrators at all, by any route.
   */
  totp?: boolean;
}

/**
 * Every account in the realm. Read uncached: `api()` installs axios-cache-interceptor, and
 * this is live second-factor state. Clearing somebody's authenticator and then reading a
 * cached row would show the administrator their own action being undone.
 */
export const getRealmUsers = async (): Promise<RRealmUser[]> => {
  try {
    const response = await api().get(`${API_BASE_URL}r/v2/auth/users`, {
      cache: false
    } as any);
    return response.data?.users ?? [];
  } catch (error) {
    errorHandler(error);
    return [];
  }
};

/**
 * Create an account on a node: `node_id` is the org, an entity with `users` delegated, or
 * a workspace under either, and exactly one of `role` / `custom_role_id` names its first
 * membership. A workspace-scoped header is no longer what places the account.
 */
export const createUser = async (userData: { email: string; nodeId: number; role?: string; customRoleId?: number; firstName: string; lastName: string; password: string; confirmPassword: string }) => {
  try {
    const workspaceId = getWorkspaceId();
    const response = await api().post(
      `${AUTH_BASE_URL}users/create`,
      {
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        password: userData.password,
        confirmPassword: userData.confirmPassword,
        node_id: userData.nodeId,
        ...(userData.customRoleId != null
          ? { custom_role_id: userData.customRoleId }
          : { role: userData.role })
      },
      {
        headers: {
          'X-Workspace-Id': workspaceId
        }
      }
    );

    successHandler(
      { response, status: response.status },
      { notifyOnSuccess: true, notifyOnFailed: true }
    );

    return response.data;
  } catch (error) {
    // The two refusals the form renders itself (grant ceiling, node does not own `users`)
    // are rethrown; everything else keeps the generic notification.
    if (grantExceedsFrom403(error) || managedByFrom409(error)) throw error;
    return errorHandler(error);
  }
};

/**
 * Take one person off **this workspace**, leaving their account alone.
 *
 * Not the same action as deactivating them, and the difference matters on a multi-tenant
 * platform: deactivation flips `enabled=false` on the Keycloak account, so they cannot sign
 * in anywhere, in any organisation. This deletes one `r_workspace_users` row. The account
 * stays enabled, every other workspace they belong to is untouched, and they simply no
 * longer reach this one.
 *
 * `userId` is the Keycloak `sub`, which is what memberships are keyed on.
 *
 * The server applies the grant rule backwards: you may remove somebody strictly below your
 * own tier, and never yourself. It answers 404 rather than 403 for a workspace outside your
 * scope, so that a workspace id cannot be probed for existence.
 */
export const removeUserFromWorkspace = async (userId: string) => {
  try {
    const workspaceId = getWorkspaceId();
    const auth = sessionStorage.getItem('auth');
    const tmpAuth = auth ? JSON.parse(auth).current : {};

    const response = await api().delete(
      `${API_BASE_URL}workspace/users/${userId}`,
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tmpAuth.access_token}`,
          // The route is gated `workspace="required"`. Sent as a header as well as a query
          // parameter because the two are read in that order and deployments differ in
          // which they honour.
          ...(workspaceId ? { 'X-Workspace-Id': String(workspaceId) } : {})
        },
        params: workspaceId ? { workspace_id: workspaceId } : undefined
      }
    );

    successHandler(
      { response, status: response.status },
      { notifyOnSuccess: true, notifyOnFailed: true }
    );
    return response.data;
  } catch (error) {
    return errorHandler(error);
  }
};

export const deleteUsers = async (ids: any[]) => {
  try {
    const user = sessionStorage.getItem('auth');
    const tmpAuth = user ? JSON.parse(user).current : {};

    // Perform the DELETE request with a list of IDs in the request body
    const response = await axios.delete(
      `${AUTH_BASE_URL}users`,
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tmpAuth.access_token}`
        },
        data: {
          ids: ids  // Send the list of IDs in the request body
        }
      }
    );

    const { status, data } = response;

    // Handle the success response
    successHandler(
      { response, status: response.status },
      {
        notifyOnSuccess: true,
        notifyOnFailed: true
      }
    );

    return data.message;  // Return the success message from the backend
  } catch (error) {
    // Handle the error response
    return errorHandler(error);
  }
};

/**
 * The console's node-scoped list: everyone with a membership at or under the org/entity, from
 * the paged members route, reshaped into the `{ users, pagination }` form the workspace list
 * answers so the table and its redux slice need no second path.
 */
export interface NodeListFilters { role?: string; status?: 'active' | 'suspended'; nodeId?: number }

const getNodeList = async ({ page, items, globalFilter, nodeId, level, filters }: { page: number; items: number; globalFilter: string; nodeId: number; level: 'organisation' | 'entity'; filters?: NodeListFilters }) => {
  try {
    const base = level === 'organisation' ? 'organisations' : 'entities';
    const response = await api().get(`${API_BASE_URL}r/v2/${base}/${nodeId}/members`, {
      params: { 
        paged: true, 
        page, 
        total: items, 
        ...(globalFilter ? { q: globalFilter } : {}),
        ...(filters?.role ? { role: filters.role } : {}),
        ...(filters?.status ? { status: filters.status } : {}),
        ...(filters?.nodeId ? { node_id: filters.nodeId } : {})
      },
      cache: false
    } as any);
    const data = response.data;
    return {
      users: (data.items ?? []).map((m: any) => ({
        id: m.user_id,
        email: m.email,
        firstName: m.first_name ?? '',
        lastName: m.last_name ?? '',
        enabled: m.enabled,
        // `null` = Keycloak was unreadable: stays undefined so the column says "unknown".
        totpEnabled: m.totp_enabled ?? undefined,
        emailVerified: m.email_verified ?? undefined,
        accountStatus: m.account_status,
        lastLoginAt: m.last_login_at ?? null,
        lastSeenAt: m.last_seen_at ?? null,
        roles: (m.memberships ?? []).map((n: any) => n.role ?? 'custom'),
        memberships: (m.memberships ?? []).map((n: any) => ({ 
          ...n, 
          custom_role_id: n.custom_role_id ?? null 
        }))
      })),
      pagination: {
        current_page: data.page,
        prev_page: data.page > 1 ? data.page - 1 : null,
        total_records: data.total,
        total_pages: data.total_pages
      },
      stats: undefined
    };
  } catch (error) {
    return errorHandler(error);
  }
};

/** The number of accounts the console's Users table lists: the paged members route's `total`. */
export const getNodeUserCount = async (nodeId: number, level: 'organisation' | 'entity'): Promise<number> => {
  const base = level === 'organisation' ? 'organisations' : 'entities';
  const response = await api().get(`${API_BASE_URL}r/v2/${base}/${nodeId}/members`, {
    params: { paged: true, page: 1, total: 1 },
    cache: false
  } as any);
  return Number(response.data.total);
};

const getList = async ({ page, items, sorting, globalFilter, filter, roles, nodeId, level, filters }: { page: number; items: number; sorting: string; globalFilter: string; filter?: any, roles?: string[], nodeId?: number, level?: 'organisation' | 'entity', filters?: NodeListFilters }) => {
  if (nodeId && level) {
    return getNodeList({ page, items, globalFilter, nodeId, level, filters });
  }
  try {

    let url = `${API_BASE_URL}workspace/users?page=${page}&total=${items}`;
    if (sorting) {
      url += `&sorting=${JSON.stringify(sorting)}`;
    }
    if (globalFilter) {
      url += `&q=${globalFilter}`;
    }
    if (filter) {
      url += `&filter=${JSON.stringify(filter)}`;
    }
    if (roles) {
      url += `&roles=${roles.join(',')}`;
    }

    const user = sessionStorage.getItem('auth');
    const tmpAuth = user ? JSON.parse(user).current : {};
    const response = await api().get(
      url,
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tmpAuth.access_token}`
        }
      }
    );

    const { status, data } = response;
    successHandler(
      { response, status: response.status },
      {
        notifyOnSuccess: false,
        notifyOnFailed: true
      }
    );
    return data;
  } catch (error) {
    return errorHandler(error);
  }
};

export const getUserDetails = async (id: any) => {
  try {
    const user = sessionStorage.getItem('auth');
    const tmpAuth = user ? JSON.parse(user).current : {};

    // Perform the DELETE request with a list of IDs in the request body
    const response = await api().get(
      `${API_BASE_URL}user/details`,
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tmpAuth.access_token}`,
          'X-Keycloak-User-Id': id
        }
      }
    );

    const { status, data } = response;

    // Handle the success response
    successHandler(
      { response, status: response.status },
      {
        notifyOnSuccess: false,
        notifyOnFailed: true
      }
    );

    return data;  // Return the success message from the backend
  } catch (error) {
    // Handle the error response
    return errorHandler(error);
  }
};

// `updateUser` and `updatePassword` are deleted. Both called `PUT /r/v2/auth/user/{id}`,
// which the backend has deleted: it refused the caller's own account, and the profile page
// was its only caller. Your own name and password go through `updateOwnName` and
// `resetOwnPassword` in mfa.service.

export const updateUserRole = async (id: string, dataItem: any) => {
  try {
    const user = sessionStorage.getItem('auth');
    const tmpAuth = user ? JSON.parse(user).current : {};

    // Perform the DELETE request with a list of IDs in the request body
    const response = await api().put(
      `${API_BASE_URL}user/edit-roles`,
      dataItem,
      {
        headers: {
          'X-Keycloak-User-Id': id
        }
      }
    );

    const { status, data } = response;

    // Handle the success response
    successHandler(
      { response, status: response.status },
      {
        notifyOnSuccess: true,
        notifyOnFailed: true
      }
    );

    return data.database_info;  // Return the success message from the backend
  } catch (error) {
    // Handle the error response
    return errorHandler(error);
  }
};

export const getUserByMail = async (email: string) => {
  try {
    const user = sessionStorage.getItem('auth');
    const tmpAuth = user ? JSON.parse(user).current : null;
    const response = await axios.get(AUTH_BASE_URL + 'users-keycloak/email', {
      headers: {
        Authorization: `Bearer ${tmpAuth.access_token}`,
        'X-Email': email
      }
    });
    return {
      data: response.data,
      status: 200
    };
  } catch (error: any) {
    return errorHandler(error);
  }
};

// `tokenCheck` (`GET invitation/verify/{token}`) is gone. Its only caller was the
// password-reset page, which is deleted. See the note on `resetPassword` below.
// Invitations live in `invitation.service.ts`: one system, issued by an organisation (or
// by the platform for a support admin) and answered under `/invitations`.

// Self-service password reset has no successor on the V2 surface, so both halves of the
// old flow are gone rather than repointed:
//
//   - `POST users/reset-invite` (mail me a reset link): 404, nothing replaces it.
//   - `POST users/reset-password` ({token, password, confirm_password}): 404. The
//     similarly named `POST /r/v2/auth/users/reset-password` is **not** its successor: it
//     takes {email, password, confirm_password}, carries no token, and returns 401 without
//     a bearer token, so it changes *your own* password while signed in. There is no route
//     that mints or redeems an emailed reset token.
//
// A Superadmin can reset another account's password through
// `PUT /r/v2/auth/users/{email}/reset-password`, which is what `/auth/reset` now points a
// locked-out user at. Raised as a backend gap.

export const handleMultiFactorAuth = async (id: string, dataItem: any) => {
  try {
    const user = sessionStorage.getItem('auth');
    const tmpAuth = user ? JSON.parse(user).current : {};

    const response = await axios.put(
      `${AUTH_BASE_URL}users/${id}/handle-mfa`,
      dataItem
      ,
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tmpAuth.access_token}`
        }
      }
    );
    const { status, data } = response;

    // Handle the success response
    successHandler(
      { response, status: response.status },
      {
        notifyOnSuccess: true,
        notifyOnFailed: true
      }
    );

    return data;  // Return the success message from the backend
  } catch (error) {
    // Handle the error response
    return errorHandler(error);
  }
};

export const getMe = async () => {
  try {
    const response = await api().get(`${API_BASE_URL}r/v2/users/me`);
    return response.data;
  } catch (error) {
    return errorHandler(error);
  }
};

export default {
  getRoleNames,
  getList,
  deleteUsers,
  getUserDetails,
  getUserByMail,
  getRealmUsers,
  updateUserRole,
  handleMultiFactorAuth,
  getMe
};